/**
 * recoveryKit.ts — the client half of the Recovery Kit.
 *
 * The kit is 24 characters generated in this browser and shown to the member
 * once. From it we derive an AES key, wrap the owner's X25519 private key,
 * and upload only the ciphertext. The code itself never touches the network,
 * so what the server holds is something it cannot open. That is the whole
 * design: a member who kept their kit restores themselves on a new phone with
 * nobody's permission and no waiting.
 *
 * WHERE THE PRIVATE KEY COMES FROM. The member app stores the owner's key in
 * IndexedDB under 'legacy-architect-keys' → 'identity-keys', as an hpke-js
 * wrapper object whose `.key` is the raw 32 bytes. That is verified against a
 * real browser in verify/key-probe.mjs, not inferred from the source — the
 * comment in src/client/auth/passkey.ts says the key is stored
 * "non-extractable", and it is not. See RECOVERY.md; the storage shape is
 * existing behaviour and is deliberately not changed here.
 */
import { KIT_ALPHABET, KIT_LENGTH, KIT_PBKDF2_ITERATIONS, formatKit, kitHint, normalizeKit } from './kitFormat';

const INDEXED_DB_NAME = 'legacy-architect-keys';
const INDEXED_DB_STORE = 'identity-keys';
const INDEXED_DB_VERSION = 1;

export { formatKit, normalizeKit, kitHint, KIT_LENGTH };

/**
 * A fresh kit. Rejection sampling rather than a modulo of a random byte,
 * because 256 is not a multiple of 32 — a modulo would make the first eight
 * symbols of the alphabet very slightly likelier, which is the sort of thing
 * that is embarrassing to explain later and free to avoid now.
 */
export function generateKitCode(): string {
  const out: string[] = [];
  const buf = new Uint8Array(1);
  while (out.length < KIT_LENGTH) {
    crypto.getRandomValues(buf);
    const v = buf[0];
    if (v >= 256 - (256 % KIT_ALPHABET.length)) continue;
    out.push(KIT_ALPHABET[v % KIT_ALPHABET.length]);
  }
  return out.join('');
}

async function deriveWrappingKey(code: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(normalizeKit(code)),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** The raw owner private key this device holds, or null if it has none. */
export async function readOwnerPrivateKeyBytes(email: string): Promise<Uint8Array | null> {
  const idb = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
  if (idb === undefined) return null;

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = idb.open(INDEXED_DB_NAME, INDEXED_DB_VERSION);
    req.onupgradeneeded = (): void => {
      const d = req.result;
      if (!d.objectStoreNames.contains(INDEXED_DB_STORE)) {
        d.createObjectStore(INDEXED_DB_STORE, { keyPath: 'email' });
      }
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void => reject(req.error ?? new Error('IndexedDB open failed'));
  });

  const record = await new Promise<{ key?: { key?: unknown } } | undefined>((resolve, reject) => {
    const tx = db.transaction(INDEXED_DB_STORE, 'readonly');
    const get = tx.objectStore(INDEXED_DB_STORE).get(email);
    get.onsuccess = (): void => resolve(get.result as { key?: { key?: unknown } } | undefined);
    get.onerror = (): void => reject(get.error ?? new Error('IndexedDB read failed'));
  });

  const inner = record?.key?.key;
  return inner instanceof Uint8Array && inner.byteLength === 32 ? inner : null;
}

/** Put a restored key back where the app expects to find it. */
export async function writeOwnerPrivateKeyBytes(email: string, bytes: Uint8Array): Promise<void> {
  const idb = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
  if (idb === undefined) throw new Error('This browser cannot store your key.');

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = idb.open(INDEXED_DB_NAME, INDEXED_DB_VERSION);
    req.onupgradeneeded = (): void => {
      const d = req.result;
      if (!d.objectStoreNames.contains(INDEXED_DB_STORE)) {
        d.createObjectStore(INDEXED_DB_STORE, { keyPath: 'email' });
      }
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void => reject(req.error ?? new Error('IndexedDB open failed'));
  });

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(INDEXED_DB_STORE, 'readwrite');
    // The exact shape the app already reads — an hpke-js style wrapper with
    // the raw bytes inside. Restoring into a different shape would leave the
    // member signed in and still unable to read their own replies.
    tx.objectStore(INDEXED_DB_STORE).put({
      email,
      key: {
        key: bytes,
        type: 'private',
        extractable: true,
        algorithm: { name: 'X25519' },
        usages: ['deriveBits'],
      },
      registeredAt: new Date().toISOString(),
    });
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => reject(tx.error ?? new Error('IndexedDB write failed'));
  });
}

export interface WrappedKit {
  wrappedKey: string;
  salt: string;
  iv: string;
  iterations: number;
  hint: string;
}

function toB64(bytes: Uint8Array | ArrayBuffer): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  for (const b of u8) s += String.fromCharCode(b);
  return btoa(s);
}

function fromB64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

/** Wrap this device's private key to a kit code, ready to upload. */
export async function wrapKeyForKit(privateKeyBytes: Uint8Array, code: string): Promise<WrappedKit> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveWrappingKey(code, salt, KIT_PBKDF2_ITERATIONS);
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    privateKeyBytes as BufferSource,
  );
  return {
    wrappedKey: toB64(ct),
    salt: toB64(salt),
    iv: toB64(iv),
    iterations: KIT_PBKDF2_ITERATIONS,
    hint: kitHint(normalizeKit(code)),
  };
}

/**
 * Open a wrapped key with a kit code. A wrong code fails the AES-GCM tag and
 * throws — there is no way to distinguish "wrong code" from "corrupt blob",
 * and no server round-trip that could tell an attacker which it was.
 */
export async function unwrapKeyWithKit(
  blob: { wrappedKey: string; salt: string; iv: string; iterations: number },
  code: string,
): Promise<Uint8Array> {
  const key = await deriveWrappingKey(code, fromB64(blob.salt), blob.iterations);
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromB64(blob.iv) as BufferSource },
    key,
    fromB64(blob.wrappedKey) as BufferSource,
  );
  const bytes = new Uint8Array(pt);
  if (bytes.byteLength !== 32) throw new Error('That kit did not open your key.');
  return bytes;
}
